"use client"
import { useId, useEffect, useRef, useCallback, HTMLProps, Suspense } from "react";
import * as Popover from "@radix-ui/react-popover";
import { SearchResultProps } from "@/utils/types";
import { cn } from "@/utils/helpers";
import { useSearchStore } from "@/store/search";
import Overlay from "@/components/common/Overlay";
import SearchForm from "./SearchForm";
import SearchResults from "./SearchResults";
import styles from "../Search.module.css";

interface SearchMainProps extends HTMLProps<HTMLElement> {
	onResultClick?: (result: SearchResultProps) => void,
	onInputChange?: (value: boolean) => void,
	onFocusChange?: (value: boolean) => void,
}

export default function SearchMain({
	// onResultClick,
	onInputChange,
	onFocusChange,
	className,
} : SearchMainProps) {
	const searchBackId = useId();
	const searchQuery = useSearchStore(store => store.searchQuery);
	const searchFocus = useSearchStore(store => store.searchFocus);
	const setSearchFocus = useSearchStore(store => store.setSearchFocus);
	const showSearchResults = useSearchStore(store => store.showSearchResults);
	const setShowSearchResults = useSearchStore(store => store.setShowSearchResults);
	const searchFormRef = useRef<HTMLFormElement>(null);
	const popoverContentRef = useRef<HTMLDivElement>(null);

	function handleOpenAutoFocus(event: any) {
		return event.preventDefault();
	};

	function handleInteractOutside(event: any) {
		if(event?.target?.id === "SearchFormField") {
			// If clicking search field, prevent default and return
			return event.preventDefault();
		} else if(event?.target?.id === searchBackId) {
			// If clicking back button, prevent default and don't return
			setShowSearchResults(false);
			return event.preventDefault();
		}
		setShowSearchResults(false);
	};

	const handleResultNavigate = useCallback((event: KeyboardEvent) => {
		event.preventDefault();
		const focusedElem = document.activeElement as HTMLButtonElement;
		const popoverContent = popoverContentRef.current;
		if(!popoverContent || !showSearchResults) return;
		// Define array of result list elements
		const resultElems = Array.from(popoverContent.querySelectorAll<HTMLButtonElement>("button"));
		if (resultElems.length === 0) return;
		// Check if currently focused element is a result list element
		const isFocusedInResults = focusedElem && resultElems.includes(focusedElem);
		
		let nextIndex;
		// If focused element is a result list item
		if(isFocusedInResults) {
			const currentIndex = resultElems.indexOf(focusedElem);
			if(event.key === "ArrowDown") {
				nextIndex = (currentIndex + 1) % resultElems.length;
			} else {
				nextIndex = (currentIndex - 1 + resultElems.length) % resultElems.length;
			}
		} else {
			nextIndex = 0;
		}
		const nextActiveElem = resultElems[nextIndex];
		nextActiveElem?.focus();
	}, [popoverContentRef, showSearchResults]);

	const handleResultClose = useCallback((_event: KeyboardEvent) => {
		if(showSearchResults) {
			searchFormRef?.current?.querySelector("input")?.focus();
			setShowSearchResults(false);
		}
	}, [searchFormRef, showSearchResults]);

	const handleFocus = () => {
		setSearchFocus(true);
	};

	const handleBlur = () => {
		setSearchFocus(false);
	};

	useEffect(() => {
		if(onInputChange) onInputChange(Boolean(searchQuery?.length));
	}, [searchQuery]);

	useEffect(() => {
		if(onFocusChange) onFocusChange(searchFocus);
	}, [searchFocus]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if((event.key === "ArrowDown" || event.key === "ArrowUp")) {
				handleResultNavigate(event);
			} else if(event.key === "Escape" ) {
				handleResultClose(event);
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleResultNavigate, handleResultClose]);

	return (
		<Popover.Root
			open={showSearchResults}
		>
			<div
				className={cn(
					styles.Search,
					showSearchResults ? styles.Search_searching : null,
					searchFocus ? styles.Search_focus : null,
					className
				)}
			>
				<Popover.Anchor
					className={styles.SearchAnchor}
				>
					<Suspense>
						<SearchForm
							ref={searchFormRef}
							onInputFocus={handleFocus}
							onInputBlur={handleBlur}
							onShowResults={setShowSearchResults}
						/>
					</Suspense>
				</Popover.Anchor>
				{/* <Popover.Portal> */}
					<Popover.Content
						asChild
						ref={popoverContentRef}
						align="start"
						side="top"
						sideOffset={4}
						onOpenAutoFocus={handleOpenAutoFocus}
						onInteractOutside={handleInteractOutside}
						className={styles.SearchPopoverContent}
					>
						<Overlay>
							<SearchResults
								onShowResults={setShowSearchResults}
							/>
						</Overlay>
					</Popover.Content>
				{/* </Popover.Portal> */}
			</div>
		</Popover.Root>
	)
}
